import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminErrorBanner,
  AdminPageLayout,
  AdminPagination,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTr,
} from "@/components/admin";
import { fetchSoundRawAudioReviews, type AdminSoundRawAudioReviewItem, type AdminSoundValidationFilter } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 20;
const validationFilters = [
  ["", "전체"],
  ["UNREVIEWED", "미검수"],
  ["CONFIRMED", "확인"],
  ["CORRECTED", "수정"],
  ["FALSE_POSITIVE", "오탐"],
  ["UNDETERMINED", "보류"],
] as const;

const formatConfidence = (value: number | null) => value == null ? "-" : `${Math.round(value * 100)}%`;
const formatFileSize = (bytes: number) => bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

const SoundRawAudioReviewsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminSoundRawAudioReviewItem[]>([]);
  const [filter, setFilter] = useState<"" | AdminSoundValidationFilter>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const result = await fetchSoundRawAudioReviews({
        validationResultCode: filter || undefined,
        page: page - 1,
        size: PAGE_SIZE,
      });
      setItems(result.items);
      setTotalPages(Math.max(1, result.totalPages));
      setTotalCount(result.totalElements);
    } catch (error) {
      setItems([]);
      setErrorMessage(error instanceof Error ? error.message : "원음 검수 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  return (
    <AdminPageLayout
      title="원음 검수함"
      subtitle="비식별 이벤트 단위로 원음을 재생하고 모델 결과를 판정합니다."
      actions={<AdminButton variant="primary" size="sm" icon={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} onClick={load} disabled={loading}>새로고침</AdminButton>}
    >
      <AdminErrorBanner message={errorMessage} />
      <AdminCard className="mt-4 overflow-hidden">
        <div className="flex flex-wrap gap-2 border-b border-slate-100 p-4">
          {validationFilters.map(([value, label]) => (
            <AdminButton key={value || "all"} variant={filter === value ? "soft" : "ghost"} size="sm" onClick={() => setFilter(value)}>{label}</AdminButton>
          ))}
        </div>
        <AdminTable
          headers={["이벤트", "분석 시각", "대표 결과", "모델", "원음", "검수 상태", ""]}
          loading={loading}
          emptyMessage={!loading && items.length === 0 ? "현재 API 저장소 인스턴스에 조건과 일치하는 원음 검수 대상이 없습니다." : undefined}
        >
          {items.map((item) => (
            <AdminTr key={item.soundAnalysisEventId} className="hover:bg-slate-50">
              <AdminTd><span className="text-xs font-bold text-slate-700">#{item.soundAnalysisEventId}</span></AdminTd>
              <AdminTd><span className="whitespace-nowrap text-xs text-slate-600">{formatDateTime(item.analyzedAt, 0, 16)}</span></AdminTd>
              <AdminTd>
                <p className="text-xs font-semibold text-slate-800">{item.representativeDisplayLabel ?? item.representativeLabelCode ?? "-"}</p>
                <p className="mt-0.5 text-xs text-slate-400">{item.representativeGroupCode ?? "-"} · {formatConfidence(item.representativeConfidence)}</p>
              </AdminTd>
              <AdminTd><span className="text-xs text-slate-600">{item.modelName ?? "-"} {item.modelVersion ?? ""}</span></AdminTd>
              <AdminTd><span className="text-xs text-slate-600">{(item.rawAudioDurationMs / 1000).toFixed(1)}초 · {formatFileSize(item.rawAudioFileSizeBytes)}</span></AdminTd>
              <AdminTd><AdminStatusBadge group="SOUND_VALIDATION" code={item.validationResultCode ?? "UNREVIEWED"} label={item.validationResultCode ?? "미검수"} /></AdminTd>
              <AdminTd><AdminButton size="sm" variant="secondary" icon="ri-arrow-right-line" onClick={() => navigate(`/admin/sound-raw-audio-reviews/${item.soundAnalysisEventId}`)}>검수</AdminButton></AdminTd>
            </AdminTr>
          ))}
        </AdminTable>
        <AdminPagination currentPage={page} totalPages={totalPages} totalCount={totalCount} pageSize={PAGE_SIZE} itemLabel="건" onPageChange={setPage} />
      </AdminCard>
    </AdminPageLayout>
  );
};

export default SoundRawAudioReviewsPage;
